import type { PermissionKey } from "@/constants/permissions"
import {
  usePermissionGuard,
  type PermissionGuard,
} from "@/hooks/usePermissionGuard"
import {
  QueryClientContext,
  type MutateOptions,
  type MutationFunctionContext,
  type UseMutationResult,
} from "@tanstack/react-query"
import { useCallback, useContext } from "react"

export type GuardedMutationResult<TData, TError, TVariables, TContext> =
  UseMutationResult<TData, TError, TVariables, TContext> & PermissionGuard

const PERMISSION_DENIED_SYMBOL = Symbol.for("turnus.permissionDenied")

/**
 * Error type produced when a guarded mutation runs without the required
 * permission. `mutateAsync` throws it; `mutate` passes it to the caller's
 * `onError`. Callers can check via `isPermissionDenied(err)` to suppress
 * their own generic error toast.
 */
export class PermissionDeniedError extends Error {
  readonly permission: PermissionKey
  readonly [PERMISSION_DENIED_SYMBOL] = true
  constructor(permission: PermissionKey) {
    super(`Missing permission: ${permission}`)
    this.name = "PermissionDeniedError"
    this.permission = permission
  }
}

export function isPermissionDenied(error: unknown): boolean {
  return (
    !!error &&
    typeof error === "object" &&
    (error as Record<symbol, unknown>)[PERMISSION_DENIED_SYMBOL] === true
  )
}

/**
 * Silent hook-layer safety net for a TanStack mutation. When the current user
 * lacks the given permission, `mutate` / `mutateAsync` short-circuit before
 * touching the network — no toast, no server round-trip.
 *
 * This is the LAST line of defense. The primary UX pattern for permissions is
 * HIDE at the source: PermissionGate around the button, RequirePermission
 * around the route, filter on the sidebar entry. If a click reaches this hook,
 * we've already leaked a button we shouldn't have — fail silently here (no
 * user-facing toast) and log so the leak gets fixed.
 *
 * The caller's `onError` still fires with a PermissionDeniedError so any local
 * cleanup (isUploading flags, optimistic rollbacks) runs.
 *
 * Kill-switch inherits via usePermissionGuard → useUser().hasPermission →
 * TEAM_MANAGEMENT flag: flag off → every mutation passes (backend does the same).
 */
export function useGuardedMutation<TData, TError, TVariables, TContext>(
  mutation: UseMutationResult<TData, TError, TVariables, TContext>,
  permission: PermissionKey,
): GuardedMutationResult<TData, TError, TVariables, TContext> {
  const guard = usePermissionGuard(permission)
  // Read the client via context rather than useQueryClient(), which throws
  // outside a QueryClientProvider (tests that pass a stubbed mutation).
  const queryClient = useContext(QueryClientContext)

  // Runs the caller's onError/onSettled for a blocked call and returns the
  // error. Since TanStack Query 5.8x these callbacks also receive a `context`
  // (client/meta/mutationKey); no mutation ran here, so only the client is set.
  const reportBlocked = useCallback(
    (
      variables: TVariables,
      options: MutateOptions<TData, TError, TVariables, TContext> | undefined,
    ) => {
      // eslint-disable-next-line no-console -- flag ungated leaks in dev/logs
      console.warn(
        `[useGuardedMutation] blocked ${permission} — button should have been hidden`,
      )
      const err = new PermissionDeniedError(permission)
      const context = {
        client: queryClient,
        meta: undefined,
      } as MutationFunctionContext
      options?.onError?.(
        err as unknown as TError,
        variables,
        undefined,
        context,
      )
      options?.onSettled?.(
        undefined,
        err as unknown as TError,
        variables,
        undefined,
        context,
      )
      return err
    },
    [permission, queryClient],
  )

  // Key on the methods, not `mutation`: useMutation() returns a new object
  // every render, but its mutate/mutateAsync stay stable, so the guarded
  // versions do too and callers can memoize on them.
  const { mutate, mutateAsync } = mutation

  const guardedMutate = useCallback<typeof mutate>(
    (...args) => {
      if (!guard.canRun) {
        reportBlocked(args[0] as TVariables, args[1])
        return
      }
      return mutate(...args)
    },
    [guard.canRun, mutate, reportBlocked],
  )

  const guardedMutateAsync = useCallback<typeof mutateAsync>(
    async (...args) => {
      if (!guard.canRun) {
        throw reportBlocked(args[0] as TVariables, args[1])
      }
      return mutateAsync(...args)
    },
    [guard.canRun, mutateAsync, reportBlocked],
  )

  return {
    ...mutation,
    mutate: guardedMutate,
    mutateAsync: guardedMutateAsync,
    ...guard,
  }
}
